import { mutationOptions } from '@tanstack/react-query';
import { payment_links as paymentLinks } from '#proto/index.qonto.cards_acceptance';
import { client } from '#queries/utils';
import { paymentLinksQueries } from './queries';
import type { ApiCreatePaymentLinkRequest, ApiDeactivateConnectionRequest } from './types';

const { ApiDeactivateConnectionRequest: DeactivateRequest, ApiDeactivateConnectionResponse } =
  paymentLinks.connections.firstparty.v2;

const {
  ApiCreatePaymentLinkRequest: CreatePaymentLinkRequest,
  ApiCreatePaymentLinkResponse,
  ApiDeactivatePaymentLinkResponse,
} = paymentLinks.firstparty.v4;

const { ApiActivateMethodResponse, ApiDeactivateMethodResponse } =
  paymentLinks.methods.firstparty.v1;

export async function createPaymentLink(request: ApiCreatePaymentLinkRequest) {
  const data = await client('/payment_links/v4/payment_links', {
    method: 'POST',
    body: CreatePaymentLinkRequest.toJSON(request),
  });

  const response = ApiCreatePaymentLinkResponse.fromJSON(data);
  if (!response.paymentLink) {
    throw new Error('Payment link creation failed');
  }
  return response.paymentLink;
}

function createPaymentLinkMutation() {
  return mutationOptions({
    mutationFn: createPaymentLink,
  });
}

export async function deactivatePaymentLink(paymentLinkId: string) {
  const data = await client(`/payment_links/v4/payment_links/${paymentLinkId}/deactivate`, {
    method: 'PATCH',
  });

  const response = ApiDeactivatePaymentLinkResponse.fromJSON(data);
  if (!response.paymentLink) {
    throw new Error('Payment link deactivation failed');
  }
  return response.paymentLink;
}

function deactivatePaymentLinkMutation() {
  return mutationOptions({
    mutationFn: deactivatePaymentLink,
  });
}

function deactivateConnection() {
  return mutationOptions({
    mutationFn: async (request: ApiDeactivateConnectionRequest) => {
      const data = await client(`/payment_links/v2/mollie/connections/${request.id}/deactivate`, {
        method: 'PATCH',
        body: DeactivateRequest.toJSON(request),
      });

      return ApiDeactivateConnectionResponse.fromJSON(data);
    },
    onSuccess: (_data, _variables, _result, context) => {
      return context.client.invalidateQueries({
        queryKey: paymentLinksQueries.connections(),
      });
    },
  });
}

function activateMethod() {
  return mutationOptions({
    mutationFn: async (id: string) => {
      const data = await client(`/payment_links/v1/methods/${id}/activate`, {
        method: 'PATCH',
      });

      return ApiActivateMethodResponse.fromJSON(data);
    },
    onSuccess: (_data, _variables, _result, context) => {
      return context.client.invalidateQueries({
        queryKey: paymentLinksQueries.methodLists(),
      });
    },
  });
}

function deactivateMethod() {
  return mutationOptions({
    mutationFn: async (id: string) => {
      const data = await client(`/payment_links/v1/methods/${id}/deactivate`, {
        method: 'PATCH',
      });

      return ApiDeactivateMethodResponse.fromJSON(data);
    },
    onSuccess: (_data, _variables, _result, context) => {
      return context.client.invalidateQueries({
        queryKey: paymentLinksQueries.methodLists(),
      });
    },
  });
}

export const paymentLinksMutations = {
  createPaymentLink: createPaymentLinkMutation,
  deactivatePaymentLink: deactivatePaymentLinkMutation,
  deactivateConnection,
  activateMethod,
  deactivateMethod,
};
